import { Head } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import PageHeader from '@/Components/PageHeader';
import FormSelect from '@/Components/FormSelect';
import Button from '@/Components/Button';
import {
    UserIcon,
    CalendarDaysIcon,
    ArrowDownTrayIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

export default function Index({ employees, selectedYear, selectedMonth, availableYears }) {
    const [employeeId, setEmployeeId] = useState('');
    const [year, setYear] = useState(selectedYear);
    const [month, setMonth] = useState(selectedMonth);
    const [search, setSearch] = useState('');

    const filteredEmployees = employees.filter(emp => {
        if (!search) return true;
        const q = search.toLowerCase();
        return emp.full_name.toLowerCase().includes(q) ||
               emp.employee_code.toLowerCase().includes(q) ||
               emp.work_location.toLowerCase().includes(q);
    });

    const selectedEmployee = employees.find(e => e.id == employeeId);

    const handleDownload = () => {
        if (!employeeId) return;
        window.location.href = `/employee-reports/pdf?employee_id=${employeeId}&year=${year}&month=${month}`;
    };

    return (
        <AppLayout>
            <Head title="Employee Reports" />

            <div className="space-y-6">
                <PageHeader
                    title="Employee Reports"
                    subtitle="Generate individual monthly attendance reports for each employee"
                />

                {/* Filters */}
                <Card>
                    <CardBody>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    <UserIcon className="w-4 h-4 inline mr-1" />
                                    Employee
                                </label>
                                <FormSelect
                                    value={employeeId}
                                    onChange={e => setEmployeeId(e.target.value)}
                                >
                                    <option value="">Select employee...</option>
                                    {filteredEmployees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.employee_code} — {emp.full_name} ({emp.work_location})
                                        </option>
                                    ))}
                                </FormSelect>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                                    Month
                                </label>
                                <FormSelect value={month} onChange={e => setMonth(e.target.value)}>
                                    {MONTHS.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </FormSelect>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                                <FormSelect value={year} onChange={e => setYear(e.target.value)}>
                                    {availableYears.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </FormSelect>
                            </div>
                        </div>

                        {selectedEmployee && (
                            <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
                                            <UserIcon className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800">{selectedEmployee.full_name}</p>
                                            <p className="text-xs text-slate-500">
                                                {selectedEmployee.employee_code} &middot; {selectedEmployee.work_location} &middot; {selectedEmployee.department}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={handleDownload}
                                        size="sm"
                                    >
                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                        Download PDF
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Employee List */}
                <Card>
                    <CardBody>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-slate-700">All Employees</h3>
                            <div className="relative">
                                <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search employees..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 w-64"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {filteredEmployees.map(emp => {
                                const isSelected = emp.id == employeeId;
                                return (
                                    <div
                                        key={emp.id}
                                        onClick={() => setEmployeeId(emp.id)}
                                        className={`p-3 rounded-xl border cursor-pointer transition-all hover:shadow-sm ${
                                            isSelected
                                                ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200'
                                                : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                                                isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                                            }`}>
                                                {emp.full_name.charAt(0)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-sm font-medium truncate ${isSelected ? 'text-emerald-800' : 'text-slate-800'}`}>
                                                    {emp.full_name}
                                                </p>
                                                <p className="text-[11px] text-slate-400 truncate">
                                                    {emp.employee_code} &middot; {emp.work_location}
                                                </p>
                                            </div>
                                            {isSelected && (
                                                <a
                                                    href={`/employee-reports/pdf?employee_id=${emp.id}&year=${year}&month=${month}`}
                                                    onClick={e => e.stopPropagation()}
                                                    className="flex-shrink-0 p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 transition"
                                                >
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5 text-white" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {filteredEmployees.length === 0 && (
                            <div className="text-center py-12">
                                <DocumentTextIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                                <p className="text-sm text-slate-400">No employees found</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>
        </AppLayout>
    );
}
